import React, { useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { splitMessageIntoLines } from '../utils/messageUtils';

const ChatMessages = ({
  isOpen,
  messages,
  isTyping,
  showScrollToBottom,
  onScroll,
  onScrollToBottom,
}) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const latestMessageKey = useMemo(() => {
    if (!messages?.length) {
      return '';
    }
    const lastMessage = messages[messages.length - 1];
    return `${lastMessage.id || 'no-id'}_${lastMessage.timestamp || 'no-time'}`;
  }, [messages]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const scrollToLatest = (behavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      });
    }

    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  };

  // Ensure we reach latest message after chat open animation completes
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const rafId = requestAnimationFrame(() => {
      scrollToLatest('auto');
    });

    const timeoutId = setTimeout(() => {
      scrollToLatest('auto');
    }, 350);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  // Auto-scroll to bottom when new messages/typing arrive while chat is open
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const rafId = requestAnimationFrame(() => {
      scrollToLatest('auto');
      requestAnimationFrame(() => {
        scrollToLatest('auto');
      });
    });

    const timeoutId = setTimeout(() => {
      scrollToLatest('auto');
    }, 120);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [latestMessageKey, isTyping, isOpen]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      // Call parent's scroll handler
      onScroll();
    }
  };

  return (
    <Box 
      ref={messagesContainerRef}
      className="chat-messages"
      onScroll={handleScroll}
    >
      {messages.map((message) => (
        <Box
          key={message.id}
          className={`message ${message.sender}`}
        >
          <Box className="message-content">
            <Typography variant="body1" className="message-text" component="div">
              {splitMessageIntoLines(message.message).map((line, index, array) => (
                <React.Fragment key={index}>
                  {line}
                  {index < array.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            <Typography variant="caption" className="message-time">
              {formatTime(message.timestamp)}
            </Typography>
          </Box>
        </Box>
      ))}
      
      {/* Typing Indicator */}
      {isTyping && (
        <Box className="message support typing-indicator">
          <Box className="message-content">
            <Typography variant="body2" className="typing-text">
              Support is typing...
            </Typography>
            <Box className="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Scroll target element */}
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatMessages;
